import {
  useUploadInvestmentFile,
  useAttachInvestmentFile,
  useDeleteInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";

interface ProcessContractFilesParams {
  investmentUuid: string;
  existingFiles: any[];
  removedUrls: Set<string>;
  newLocalFiles: File[];
  role?: "contract" | "closure_contract";
}

interface ProcessPhotoFilesParams {
  investmentUuid: string;
  existingFiles: any[];
  removedUrls: Set<string>;
  newLocalFiles: File[];
}

export const useEditFileOperations = () => {
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { mutateAsync: deleteFile } = useDeleteInvestmentFile();
  const { data: me } = useGetMe();

  const getUserId = (): string | number =>
    (me as any)?.user?.id ?? (me as any)?.id ?? "";

  const processContractFiles = async ({
    investmentUuid,
    existingFiles,
    removedUrls,
    newLocalFiles,
    role = "contract",
  }: ProcessContractFilesParams) => {
    const filesToDelete = existingFiles.filter(
      (f: any) => f?.file?.url && removedUrls.has(f.file.url),
    );
    const hasChanges = filesToDelete.length > 0 || newLocalFiles.length > 0;
    if (!hasChanges) return;

    const userId = getUserId();

    for (const file of filesToDelete) {
      if (file?.file?.uuid) {
        await deleteFile({ investmentUuid, fileUuid: file.file.uuid });
      }
    }

    await Promise.allSettled(
      newLocalFiles.slice(0, 10).map(async (contract: File) => {
        try {
          const uploaded = await uploadFile({
            file: contract,
            type: "attachment",
            parentId: userId,
          });
          const fileUuid = Array.isArray(uploaded)
            ? uploaded[0]?.uuid
            : (uploaded as any)?.uuid;
          if (fileUuid) {
            await attachFile({ investmentUuid, fileUuid, role });
          }
        } catch (error) {
          console.error("Error uploading contract:", error);
        }
      }),
    );
  };

  const processPhotoFiles = async ({
    investmentUuid,
    existingFiles,
    removedUrls,
    newLocalFiles,
  }: ProcessPhotoFilesParams) => {
    const photosToDelete = existingFiles.filter(
      (f: any) => f?.file?.url && removedUrls.has(f.file.url),
    );
    const hasChanges = photosToDelete.length > 0 || newLocalFiles.length > 0;
    if (!hasChanges) return;

    const userId = getUserId();

    for (const photo of photosToDelete) {
      if (photo?.file?.uuid) {
        await deleteFile({ investmentUuid, fileUuid: photo.file.uuid });
      }
    }

    await Promise.allSettled(
      newLocalFiles.slice(0, 10).map(async (photo: File) => {
        try {
          const uploaded = await uploadFile({
            file: photo,
            type: "image",
            parentId: userId,
          });
          const fileUuid = Array.isArray(uploaded)
            ? uploaded[0]?.uuid
            : (uploaded as any)?.uuid;
          if (fileUuid) {
            await attachFile({ investmentUuid, fileUuid, role: "photo" });
          }
        } catch {
          // pojedyncze zdjęcie nie blokuje
        }
      }),
    );
  };

  return { processContractFiles, processPhotoFiles };
};
